import React, { FC, ReactElement, ReactNode, useEffect } from 'react';
import { useTranslations } from '../../../../hooks/useTranslations';
import { MediumHeader, BigHeader } from '../../../ui/Header/Header';
import { QueryOrderTrackingArgs } from '../../../../../../__generated__/loma';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { useTheme } from '../../../../hooks/useTheme';
import EddDashWithTheme from './BlockEddDash';
import EddTimeRange from '../EddTimeRange/EddTimeRange';
import { useLogEdd } from './hooks/useLogEdd';
import {
  useAnalyticsPost,
  NarvarEventComponents,
  NarvarEventTypes,
} from '../../../../hooks/Analytics';
import styles from './BlockEdd.module.css';
import {
  usePageContext,
  useFormattedEdd,
} from '../../../../hooks/PageContext/usePageContext';

export interface BlockEddProps extends Pick<
  QueryOrderTrackingArgs,
  'orderNumbers'
> {
  featureName?: string;
  internationalDateFormatting?: boolean | null;
}

enum DateTokenType {
  ARIA_LABEL = 'ARIA_LABEL',
  DAY_OF_MONTH = 'DAY_OF_MONTH',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  MONTH = 'MONTH',
}

// Typescript can only narrow based on literals, meaning we can't determine that
// an arbitrary variable represents a key of an object. In other words, this won't narrow:
// if (token in DateTokenType). But this will: if ("DAY_OF_MONTH" in DateTokenType).
// This utility function asserts that the value of an arbitrary variable is
// in fact a key of an object T if `key in obj` is true
function isIn<T extends Record<PropertyKey, unknown>>(
  key: PropertyKey,
  obj: T,
): key is keyof T {
  return key in obj;
}

const DEFAULT_EDD_ORDER = `${DateTokenType.DAY_OF_WEEK},${DateTokenType.MONTH},${DateTokenType.DAY_OF_MONTH}`;

const BlockEdd: FC<BlockEddProps> = ({
  featureName,
  internationalDateFormatting,
}): ReactElement | null => {
  const logEvent = useAnalyticsPost();
  const { buttonsPrimaryDefault, headersMediumNone } = useTheme();

  const { state } = usePageContext();
  const { retailerMoniker } = state;

  const { blockEddDateTokenOrder: blockEddDateTokenOrderRaw } =
    useTranslations();

  const { getCurrentShipment, orderTrackingData } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const { displayDate, displayDateRange, scheduling, status } =
    currentShipment || {};
  const schedulable = scheduling?.isSchedulable && !scheduling.isScheduled;

  const isDelivered = status === 'DELIVERED';

  const eddBegin = displayDateRange?.begin || displayDate;

  useLogEdd(currentShipment, featureName);

  const atlasResponseStatus = orderTrackingData?.orders[0].atlasResponseStatus;

  useEffect(() => {
    if (atlasResponseStatus === 'CIRCUIT_BREAKER_OPEN') {
      logEvent({
        displayedEddBegin: eddBegin,
        displayedEddEnd: displayDateRange?.end,
        eventCarrierMoniker: currentShipment?.carrier,
        eventType: NarvarEventTypes.CIRCUIT_BREAKER_OPEN,
        eventComponent: NarvarEventComponents.NTH_BLOCK_EDD,
        eventTrackStatus: currentShipment?.status,
        eventTrackingNumber: currentShipment?.trackingNumber,
        pageRetailerMoniker: retailerMoniker,
        pageOrderNumber: orderTrackingData?.orders[0].orderNumber,
        pageUrl: window.location.href,
      });
    }
  }, [
    atlasResponseStatus,
    eddBegin,
    displayDateRange,
    currentShipment,
    logEvent,
    retailerMoniker,
    orderTrackingData,
  ]);

  const formatEdd = useFormattedEdd();

  if (!currentShipment || schedulable) return null;

  const [eddBeginFormatted, eddEndFormatted] = formatEdd?.formattedEdds || [];

  if (!eddBeginFormatted) return null;

  let dateValuesMappings: (Record<DateTokenType, string> | null)[] | null =
    null;
  if (eddEndFormatted) {
    // Use the short "day of week" translation for date range
    const [beginAriaLabel, beginDayOfWeekShort, , beginMonth, beginDayOfMonth] =
      eddBeginFormatted;
    const [endAriaLabel, endDayOfWeekShort, , endMonth, endDayOfMonth] =
      eddEndFormatted;

    const begin =
      beginAriaLabel && beginDayOfWeekShort && beginMonth && beginDayOfMonth
        ? {
            ARIA_LABEL: beginAriaLabel,
            DAY_OF_MONTH: beginDayOfMonth,
            DAY_OF_WEEK: beginDayOfWeekShort,
            MONTH: beginMonth,
          }
        : null;
    const end =
      endAriaLabel && endDayOfWeekShort && endMonth && endDayOfMonth
        ? {
            ARIA_LABEL: endAriaLabel,
            DAY_OF_MONTH: endDayOfMonth,
            DAY_OF_WEEK: endDayOfWeekShort,
            MONTH: endMonth,
          }
        : null;

    dateValuesMappings = [begin, end];
  } else {
    // Use the long "day of week" translation
    const [ariaLabel, , dayOfWeek, month, dayOfMonth] = eddBeginFormatted;
    const begin =
      ariaLabel && dayOfWeek && month && dayOfMonth
        ? {
            ARIA_LABEL: ariaLabel,
            DAY_OF_MONTH: dayOfMonth,
            DAY_OF_WEEK: dayOfWeek,
            MONTH: month,
          }
        : null;

    dateValuesMappings = [begin, null];
  }

  if (!dateValuesMappings[0]) return null;

  const blockEddDateTokenOrder =
    internationalDateFormatting && typeof blockEddDateTokenOrderRaw === 'string'
      ? blockEddDateTokenOrderRaw || DEFAULT_EDD_ORDER
      : DEFAULT_EDD_ORDER;

  const EddWrapper = ({ children }: { children: ReactNode }) =>
    isDelivered ? (
      <MediumHeader
        containerCustomStyle={{
          color:
            buttonsPrimaryDefault?.backgroundColor ?? headersMediumNone?.color,
        }}
      >
        {children}
      </MediumHeader>
    ) : (
      <BigHeader>{children}</BigHeader>
    );

  const renderEdd: (
    dateValuesMapping: Record<DateTokenType, string>,
  ) => ReactNode = (dateValuesMapping) => {
    if (!dateValuesMapping) return null;

    return (
      <>
        <EddWrapper>
          {blockEddDateTokenOrder.split(',').map((token) => {
            let value = null;
            if (isIn<Record<DateTokenType, string>>(token, dateValuesMapping)) {
              value = dateValuesMapping[token];
            }
            if (!value) return null;

            if (
              token === DateTokenType.DAY_OF_MONTH ||
              token === DateTokenType.MONTH ||
              token === DateTokenType.DAY_OF_WEEK
            ) {
              return (
                <span
                  key={token}
                >{`${value}${token === DateTokenType.DAY_OF_WEEK ? ',' : ''} `}</span>
              );
            }
          })}
        </EddWrapper>
        <div className={styles.eddTimeRangeContainer}>
          <EddTimeRange />
        </div>
      </>
    );
  };

  const [begin, end] = dateValuesMappings;

  return (
    <div
      data-test="eddDisplayContainer"
      className={styles.container}
      style={{
        alignItems: 'center',
      }}
    >
      {begin && (
        <div aria-label={begin.ARIA_LABEL}>
          <span aria-hidden="true">{renderEdd(begin)}</span>
        </div>
      )}
      {end && (
        <>
          <EddDashWithTheme />
          <div aria-label={end.ARIA_LABEL}>
            <span aria-hidden="true">{renderEdd(end)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default BlockEdd;
